import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import axios from 'axios'
import map from 'lodash/map'
import debounce from 'lodash/debounce'
import lowerFirst from 'lodash/lowerFirst'
import { Moment } from 'moment-timezone'
import { LocalizationContext } from '../../shared/contexts'
import { formatTenantDateTime } from '../../../utils/formatTenantDateTime'
import TenantDateTimePicker from '../../shared/TenantDateTimePicker'
import Select from '@mui/material/Select'
import Tooltip from '@mui/material/Tooltip'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import { ListItemText, MenuItem, OutlinedInput, Pagination } from '@mui/material'
import { t } from '../../../i18n'
import { getParam, applyParams, parseIsoParam, parsePageParam } from './urlParams'
import { TIME_SOURCE, timeSourceTitle } from '../../../utils/timeSource'
import DosingDifferencePercent from '../../shared/DosingDifferencePercent'
import StatusBadge from '../../shared/StatusBadge'
import ExportToExcelButton from './ExportToExcelButton'

const scope = 'command_history'

type DosingSearchOption = 'queued_order_id' | 'recipe_name' | 'product_name'
type DosingOutcome = 'all' | 'failed' | 'retried' | 'backup'
type DosingDeviation = 'all' | 'on_target' | 'over' | 'under'

interface SelectOption {
  value: number
  label: string
}

export interface DosingFilterOptions {
  feederSlots: string[]
  doseCommands: SelectOption[]
  productCategories: SelectOption[]
}

const dosingSearchOptions = [
  { label: t('recipe_name', { scope }),     value: 'recipe_name' as DosingSearchOption },
  { label: t('product_name', { scope }),    value: 'product_name' as DosingSearchOption },
  { label: t('queued_order_id', { scope }), value: 'queued_order_id' as DosingSearchOption }
]

const outcomeOptions = [
  { label: t('all_attempts', { scope }),  value: 'all' as DosingOutcome },
  { label: t('failures_only', { scope }), value: 'failed' as DosingOutcome },
  { label: t('retried_only', { scope }),  value: 'retried' as DosingOutcome },
  { label: t('backup_only', { scope }),   value: 'backup' as DosingOutcome }
]

const deviationOptions = [
  { label: t('any_deviation', { scope }),   value: 'all' as DosingDeviation },
  { label: t('on_target_only', { scope }),  value: 'on_target' as DosingDeviation },
  { label: t('over_dosed_only', { scope }), value: 'over' as DosingDeviation },
  { label: t('under_dosed_only', { scope }),value: 'under' as DosingDeviation }
]

// "solid" / "sauce" cover a whole group of commands; the numeric values narrow
// to one specific command, and are appended from the server's list.
const DOSING_TYPE_GROUPS = [
  { label: t('all_types', { scope }), value: 'all' },
  { label: t('solid', { scope }),     value: 'solid' },
  { label: t('sauce', { scope }),     value: 'sauce' }
]

const HOUR_OPTIONS = Array.from({ length: 24 }, (_value, hour) => ({
  label: `${String(hour).padStart(2, '0')}:00`,
  value: String(hour)
}))

const SLOW_CYCLE_OPTIONS = [
  { label: t('any_duration', { scope }), value: '' },
  { label: t('slower_than', { scope, seconds: 10 }), value: '10' },
  { label: t('slower_than', { scope, seconds: 30 }), value: '30' },
  { label: t('slower_than', { scope, seconds: 60 }), value: '60' },
  { label: t('slower_than', { scope, seconds: 120 }), value: '120' }
]

interface DosingRow {
  id: number
  queued_order_id: number | null
  recipe_name: string | null
  product_name: string | null
  feeder_slot: string | null
  planned_amount: number
  actual_dosed: number
  // sum of every attempt of the dosing operation, reported once on its final
  // row; null on the partial attempt rows of a dose split across containers
  total_dosed: number | null
  difference_percent: number | null
  amount_left: number | null
  executed_at: string | null
  dosing_failed: boolean
  // what the robot reported for the dosing substep itself — normally 0, since
  // the dosing substeps are completion markers rather than spans
  duration_seconds: number | null
  estimated_duration: number | null
  cycle_seconds: number | null
  dosing_rate: number | null
  attempt_count: number | null
  container_switched: boolean
}

// Counts are per dosing operation (one ingredient of one meal) unless named an
// attempt count; an operation that needed two tries counts once.
interface DosingSummary {
  operation_count: number
  attempt_count: number
  failed_operation_count: number
  unrecovered_operation_count: number
  container_switch_count: number
  failure_rate_percent: number | null
  within_tolerance_percent: number | null
  tolerance_percent: number
  deviation_sample_count: number
  avg_deviation_percent: number | null
  avg_abs_deviation_percent: number | null
  median_abs_deviation_percent: number | null
  avg_cycle_seconds: number | null
  p90_cycle_seconds: number | null
  avg_estimated_duration: number | null
  avg_dosing_rate: number | null
}

interface DosingFeederStat {
  feeder_slot: string | null
  attempt_count: number
  failed_attempt_count: number
  failure_rate_percent: number | null
  operation_count: number
  product_count: number
  total_dosed: number
  avg_cycle_seconds: number | null
  max_cycle_seconds: number | null
  avg_dosing_rate: number | null
  avg_abs_deviation_percent: number | null
  deviation_sample_count: number
  last_dosed_at: string | null
}

interface DosingHistoryResponse {
  durations: DosingRow[]
  totalPages: number
  summary: DosingSummary | null
  feeders: DosingFeederStat[]
}

const EMPTY_RESPONSE: DosingHistoryResponse = { durations: [], totalPages: 0, summary: null, feeders: [] }

const COLUMN_COUNT = 13
const DASH = '-'

const formatValue = (value: number | null, suffix = ''): string =>
  value == null ? DASH : `${value}${suffix}`

// Click to open, click again (or anywhere else) to close — deliberately not a
// hover tooltip, so the explanation stays put while it is being read.
const StatTile = ({ label, value, sub, help }: {
  label: string
  value: React.ReactNode
  sub?: React.ReactNode
  help: string
}) => {
  const [open, setOpen] = useState(false)

  return (
    <div className='dosing-stat'>
      <span className='dosing-stat-label'>
        {label}
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Tooltip
            open={open}
            title={help}
            arrow
            placement='top'
            disableFocusListener
            disableHoverListener
            disableTouchListener
            onClose={() => setOpen(false)}
          >
            <button
              type='button'
              className='dosing-stat-info'
              aria-label={help}
              onClick={() => setOpen(isOpen => !isOpen)}
            >
              <i className='fa fa-question-circle-o' />
            </button>
          </Tooltip>
        </ClickAwayListener>
      </span>
      <span className='dosing-stat-value'>{value}</span>
      <span className='dosing-stat-sub'>{sub}</span>
    </div>
  )
}

interface FilterSelectInputProps {
  value: string
  options: { value: string, label: string }[]
  onChange: (value: string) => void
  emptyLabel?: string
}

const FilterSelectInput = ({ value, options, onChange, emptyLabel }: FilterSelectInputProps) => (
  <Select
    className='react-select'
    value={value}
    displayEmpty={emptyLabel != null}
    onChange={opt => onChange(opt.target.value as string)}
    input={<OutlinedInput />}
    MenuProps={{ PaperProps: { style: { maxHeight: 224, width: 250 } } }}
  >
    {emptyLabel != null && (
      <MenuItem value=''>
        <ListItemText primary={emptyLabel} />
      </MenuItem>
    )}
    {options.map(option => (
      <MenuItem key={option.value} value={option.value}>
        <ListItemText primary={option.label} />
      </MenuItem>
    ))}
  </Select>
)

const FilterSelect = ({ label, ...selectProps }: FilterSelectInputProps & { label: string }) => (
  <div className='command-filter command-filter-select'>
    <span className='command-filter-label'>{label}</span>
    <FilterSelectInput {...selectProps} />
  </div>
)

// Only notable rows get a badge — a column of a hundred "OK"s would bury the
// handful of doses that actually need looking at.
const DosingResult = ({ row }: { row: DosingRow }) => {
  const attempts = row.attempt_count || 0
  const badges: React.ReactNode[] = []

  if (row.dosing_failed) {
    badges.push(<StatusBadge key='failed' label={t('failed', { scope })} variant='danger-soft' />)
  }
  if (row.container_switched) {
    badges.push(<StatusBadge key='backup' label={t('backup_used', { scope })} variant='neutral-outline' />)
  }
  if (attempts > 1) {
    badges.push(<StatusBadge key='attempts' label={`×${attempts}`} variant='neutral-outline' />)
  }

  if (badges.length === 0) return <>{DASH}</>

  return <div className='dosing-result-badges'>{badges}</div>
}

const DosingSummaryTiles = ({ summary, unit }: { summary: DosingSummary, unit: string }) => (
  <div className='dosing-summary'>
    <StatTile
      label={t('summary_doses', { scope })}
      value={summary.operation_count}
      sub={t('summary_attempts', { scope, count: summary.attempt_count })}
      help={t('help_doses', { scope })}
    />
    <StatTile
      label={t('failure_rate', { scope })}
      value={formatValue(summary.failure_rate_percent, '%')}
      sub={t('summary_failed', { scope, failed: summary.failed_operation_count, unrecovered: summary.unrecovered_operation_count })}
      help={t('help_failure_rate', { scope })}
    />
    <StatTile
      label={t('on_target', { scope })}
      value={formatValue(summary.within_tolerance_percent, '%')}
      sub={t('summary_tolerance', { scope, tolerance: summary.tolerance_percent, count: summary.deviation_sample_count })}
      help={t('help_on_target', { scope, tolerance: summary.tolerance_percent })}
    />
    <StatTile
      label={t('avg_deviation', { scope })}
      value={formatValue(summary.avg_abs_deviation_percent, '%')}
      sub={t('summary_median', { scope, median: formatValue(summary.median_abs_deviation_percent, '%') })}
      help={t('help_avg_deviation', { scope })}
    />
    <StatTile
      label={t('avg_bias', { scope })}
      value={<DosingDifferencePercent percent={summary.avg_deviation_percent} />}
      sub={t('summary_bias_hint', { scope })}
      help={t('help_avg_bias', { scope })}
    />
    <StatTile
      label={t('avg_cycle', { scope })}
      value={formatValue(summary.avg_cycle_seconds, ' s')}
      sub={t('summary_cycle_tail', { scope, p90: formatValue(summary.p90_cycle_seconds, ' s') })}
      help={t('help_avg_cycle', { scope })}
    />
    <StatTile
      label={t('avg_rate', { scope })}
      value={formatValue(summary.avg_dosing_rate, ` ${unit}/s`)}
      sub={t('summary_estimate', { scope, estimate: formatValue(summary.avg_estimated_duration, ' s') })}
      help={t('help_avg_rate', { scope })}
    />
    <StatTile
      label={t('backup_fallbacks', { scope })}
      value={summary.container_switch_count}
      sub={t('summary_fallback_hint', { scope })}
      help={t('help_backup_fallbacks', { scope })}
    />
  </div>
)

const FeederBreakdown = ({ feeders, unit }: { feeders: DosingFeederStat[], unit: string }) => {
  const localization = useContext(LocalizationContext)

  return (
    <div className='dosing-table-scroll'>
      <table className='table table-robis dosing-feeder-table'>
        <thead className='thead-robis'>
          <tr>
            <th>{t('feeder', { scope })}</th>
            <th className='dosing-num'>{t('attempts', { scope })}</th>
            <th className='dosing-num'>{t('failures', { scope })}</th>
            <th className='dosing-num'>{t('avg_deviation', { scope })}</th>
            <th className='dosing-num'>{t('avg_cycle', { scope })}</th>
            <th className='dosing-num'>{t('max_cycle', { scope })}</th>
            <th className='dosing-num'>{t('avg_rate', { scope })} ({unit}/s)</th>
            <th className='dosing-num'>{t('total_dosed', { scope })} ({unit})</th>
            <th className='dosing-num'>{t('products', { scope })}</th>
            <th className='dosing-when'>{t('last_dose', { scope })}</th>
          </tr>
        </thead>
        <tbody>
          {map(feeders, (feeder: DosingFeederStat) => (
            <tr key={feeder.feeder_slot || 'unknown'}>
              <td>{feeder.feeder_slot || DASH}</td>
              <td className='dosing-num'>{feeder.attempt_count}</td>
              <td className='dosing-num'>
                {feeder.failed_attempt_count > 0
                  ? <span className='text-danger'>{feeder.failed_attempt_count} ({formatValue(feeder.failure_rate_percent, '%')})</span>
                  : DASH}
              </td>
              <td className='dosing-num'>
                {formatValue(feeder.avg_abs_deviation_percent, '%')}
                {feeder.deviation_sample_count > 0 && (
                  <span className='dosing-sample-size'> (n={feeder.deviation_sample_count})</span>
                )}
              </td>
              <td className='dosing-num'>{formatValue(feeder.avg_cycle_seconds, ' s')}</td>
              <td className='dosing-num'>{formatValue(feeder.max_cycle_seconds, ' s')}</td>
              <td className='dosing-num'>{formatValue(feeder.avg_dosing_rate)}</td>
              <td className='dosing-num'>{feeder.total_dosed}</td>
              <td className='dosing-num'>{feeder.product_count}</td>
              <td className='dosing-when' title={timeSourceTitle(feeder.last_dosed_at, TIME_SOURCE.robot)}>
                {formatTenantDateTime(feeder.last_dosed_at, localization) || DASH}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface PageOptions {
  searchAttribute: DosingSearchOption
  searchValue: string
  currentPage: number
  feederSlot: string
  outcome: DosingOutcome
  deviation: DosingDeviation
  dosingType: string
  productCategoryId: string
  hourFrom: string
  hourTo: string
  minAmount: string
  maxAmount: string
  minCycleSeconds: string
  timeFrom: Moment | null
  timeUntil: Moment | null
}

// The query params every filter maps to. Built in one place so the URL and the
// request can never disagree about what is being filtered.
const filterParams = (options: PageOptions): Record<string, string | null> => ({
  search_attribute: options.searchAttribute === 'recipe_name' ? null : options.searchAttribute,
  search_value: options.searchValue || null,
  page: options.currentPage > 1 ? String(options.currentPage) : null,
  feeder_slot: options.feederSlot || null,
  outcome: options.outcome === 'all' ? null : options.outcome,
  deviation: options.deviation === 'all' ? null : options.deviation,
  dosing_type: options.dosingType === 'all' ? null : options.dosingType,
  product_category_id: options.productCategoryId || null,
  hour_from: options.hourFrom || null,
  hour_to: options.hourTo || null,
  min_amount: options.minAmount || null,
  max_amount: options.maxAmount || null,
  min_cycle_seconds: options.minCycleSeconds || null,
  time_from: options.timeFrom ? options.timeFrom.toISOString() : null,
  time_until: options.timeUntil ? options.timeUntil.toISOString() : null
})

// Everything except the paging and the search field, which are not filters the
// user would think of clearing.
const NARROWING_PARAMS = [
  'search_value', 'feeder_slot', 'outcome', 'deviation', 'dosing_type',
  'product_category_id', 'hour_from', 'hour_to', 'min_amount', 'max_amount',
  'min_cycle_seconds', 'time_from', 'time_until'
]

// Filters kept behind "more filters" — the narrower slices, so the common ones
// stay reachable without expanding.
const ADVANCED_PARAMS = [
  'dosing_type', 'product_category_id', 'hour_from', 'hour_to',
  'min_amount', 'max_amount', 'min_cycle_seconds'
]

const countActive = (options: PageOptions, keys: string[]): number => {
  const params = filterParams(options)
  return keys.filter(key => params[key] != null).length
}

// Identifies the filter set independently of which page of it is being viewed,
// so paging can keep the summary on screen while a filter change discards it.
const filterSignature = (options: PageOptions): string => {
  const { page: _page, ...filters } = filterParams(options)
  return JSON.stringify(filters)
}

const DosingAmountHistory = ({ weightUnit, filterOptions }: { weightUnit: string, filterOptions: DosingFilterOptions }) => {
  const localization = useContext(LocalizationContext)
  const { feederSlots, doseCommands, productCategories } = filterOptions
  const dosingTypeOptions = [
    ...DOSING_TYPE_GROUPS,
    ...doseCommands.map(command => ({ label: command.label, value: String(command.value) }))
  ]

  const defaultOptions = (): PageOptions => ({
    searchAttribute: 'recipe_name',
    searchValue: '',
    currentPage: 1,
    feederSlot: '',
    outcome: 'all',
    deviation: 'all',
    dosingType: 'all',
    productCategoryId: '',
    hourFrom: '',
    hourTo: '',
    minAmount: '',
    maxAmount: '',
    minCycleSeconds: '',
    timeFrom: null,
    timeUntil: null
  })

  const [data, setData] = useState<DosingHistoryResponse>(EMPTY_RESPONSE)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [feedersExpanded, setFeedersExpanded] = useState(false)
  const [pageOptions, setPageOptions] = useState<PageOptions>(() => {
    const urlSearchAttribute = getParam('search_attribute')
    const urlFeederSlot = getParam('feeder_slot')
    const urlOutcome = getParam('outcome')
    const urlDeviation = getParam('deviation')
    const urlDosingType = getParam('dosing_type')
    const urlCategory = getParam('product_category_id')
    const knownOption = (options: { value: string }[], value: string | null) =>
      options.some(option => option.value === value)

    return {
      ...defaultOptions(),
      searchAttribute: (knownOption(dosingSearchOptions, urlSearchAttribute)
        ? urlSearchAttribute
        : 'recipe_name') as DosingSearchOption,
      searchValue: getParam('search_value') || '',
      currentPage: parsePageParam(getParam('page')),
      feederSlot: urlFeederSlot && feederSlots.includes(urlFeederSlot) ? urlFeederSlot : '',
      outcome: (knownOption(outcomeOptions, urlOutcome) ? urlOutcome : 'all') as DosingOutcome,
      deviation: (knownOption(deviationOptions, urlDeviation) ? urlDeviation : 'all') as DosingDeviation,
      dosingType: knownOption(dosingTypeOptions, urlDosingType) ? (urlDosingType as string) : 'all',
      productCategoryId: productCategories.some(category => String(category.value) === urlCategory)
        ? (urlCategory as string)
        : '',
      hourFrom: knownOption(HOUR_OPTIONS, getParam('hour_from')) ? (getParam('hour_from') as string) : '',
      hourTo: knownOption(HOUR_OPTIONS, getParam('hour_to')) ? (getParam('hour_to') as string) : '',
      minAmount: getParam('min_amount') || '',
      maxAmount: getParam('max_amount') || '',
      minCycleSeconds: knownOption(SLOW_CYCLE_OPTIONS, getParam('min_cycle_seconds'))
        ? (getParam('min_cycle_seconds') as string)
        : '',
      timeFrom: parseIsoParam(getParam('time_from')),
      timeUntil: parseIsoParam(getParam('time_until'))
    }
  })
  const [advancedExpanded, setAdvancedExpanded] = useState(
    () => countActive(pageOptions, ADVANCED_PARAMS) > 0
  )
  const searchRef = useRef<HTMLInputElement>(null)
  const lastSignature = useRef<string>(filterSignature(pageOptions))

  useEffect(() => {
    const params = filterParams(pageOptions)
    applyParams(params, 'replace')

    // Analytical numbers must never sit under filters they were not computed
    // for, so a filter change drops the whole response. Paging keeps it, since
    // the summary and the per-feeder rows cover the filter set, not the page.
    const signature = filterSignature(pageOptions)
    if (lastSignature.current !== signature) {
      lastSignature.current = signature
      setData(EMPTY_RESPONSE)
    }

    const controller = new AbortController()
    setLoading(true)
    setError(null)
    axios.get('/admin/command_step_durations', {
      signal: controller.signal,
      params: {
        ...params,
        view: 'dosing_amount_history',
        // the URL hides these at their defaults; the request always needs them
        page: pageOptions.currentPage,
        search_attribute: pageOptions.searchAttribute,
        search_value: pageOptions.searchValue,
        format: 'json'
      }
    })
      .then(({ data }) => {
        setData(data)
        setLoading(false)
      })
      .catch((err) => {
        if (axios.isCancel(err)) return
        console.error('Failed to load dosing amount history:', err)
        setError(err?.response?.data || err?.message || 'Failed to load dosing amount history')
        // Nothing on screen may outlive a failed request — a stale page of rows
        // under the new filters reads as a real result.
        setData(EMPTY_RESPONSE)
        setLoading(false)
      })
    return () => controller.abort()
  }, [pageOptions])

  const handlePageChange = (page: number) => {
    setPageOptions(prev => ({ ...prev, currentPage: page }))
  }

  // Any filter change sends the user back to page 1 — page 7 of the previous
  // result set says nothing about the new one.
  const updateFilter = <K extends keyof PageOptions>(key: K, value: PageOptions[K]) => {
    setPageOptions(prev => ({ ...prev, [key]: value, currentPage: 1 }))
  }

  const handleSearch = useCallback(debounce((newSearchValue: string) => {
    setPageOptions(prev => ({ ...prev, searchValue: newSearchValue, currentPage: 1 }))
  }, 300), [])

  const handleSearchOptionChange = (newSearchOption: DosingSearchOption) => {
    setPageOptions(prev => ({ ...prev, searchAttribute: newSearchOption, searchValue: '' }))
    if (searchRef.current) searchRef.current.value = ''
  }

  const handleClearFilters = () => {
    setPageOptions(prev => ({ ...defaultOptions(), searchAttribute: prev.searchAttribute }))
    if (searchRef.current) searchRef.current.value = ''
  }

  const unit = weightUnit || 'g'
  const activeFilterCount = countActive(pageOptions, NARROWING_PARAMS)
  const activeAdvancedCount = countActive(pageOptions, ADVANCED_PARAMS)

  return (
    <div className="command-history-table">
      <div className="command-filter-bar">
        <div className="command-filter command-filter-select">
          <span className="command-filter-label">{t('search_by', { scope })}</span>
          <Select
            className='react-select'
            value={pageOptions.searchAttribute}
            onChange={opt => handleSearchOptionChange(opt.target.value as DosingSearchOption)}
            input={<OutlinedInput />}
            MenuProps={{ PaperProps: { style: { maxHeight: 224, width: 250 } } }}
          >
            {dosingSearchOptions.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <ListItemText primary={option.label} />
              </MenuItem>
            ))}
          </Select>
        </div>

        <div className="command-filter command-filter-search">
          <span className="command-filter-label">{t('search', { scope })}</span>
          <input
            type='search'
            autoFocus
            name='q'
            className='search-input form-control'
            autoComplete='off'
            placeholder={`${t('search_by', { scope })} ${lowerFirst(t(pageOptions.searchAttribute, { scope }))}...`}
            defaultValue={pageOptions.searchValue}
            onChange={e => handleSearch(e.target.value)}
            ref={searchRef}
          />
        </div>

        <FilterSelect
          label={t('feeder_slot', { scope })}
          value={pageOptions.feederSlot}
          emptyLabel={t('all_feeders', { scope })}
          options={feederSlots.map(slot => ({ value: slot, label: slot }))}
          onChange={value => updateFilter('feederSlot', value)}
        />

        <FilterSelect
          label={t('outcome', { scope })}
          value={pageOptions.outcome}
          options={outcomeOptions}
          onChange={value => updateFilter('outcome', value as DosingOutcome)}
        />

        <FilterSelect
          label={t('deviation', { scope })}
          value={pageOptions.deviation}
          options={deviationOptions}
          onChange={value => updateFilter('deviation', value as DosingDeviation)}
        />

        <div className="command-filter command-filter-dates">
          <span className="command-filter-label">{t('date_range', { scope })}</span>
          <div className="time-filter">
            <TenantDateTimePicker
              className='me-2'
              disableFuture
              label={pageOptions.timeFrom ? '' : t('from', { scope })}
              value={pageOptions.timeFrom}
              onChange={time => updateFilter('timeFrom', time)}
            />
            <TenantDateTimePicker
              disableFuture
              label={pageOptions.timeUntil ? '' : t('to', { scope })}
              value={pageOptions.timeUntil}
              onChange={time => updateFilter('timeUntil', time)}
            />
          </div>
        </div>

        <div className="command-filter command-filter-export">
          <ExportToExcelButton
            params={{
              ...filterParams(pageOptions),
              view: 'dosing_amount_history',
              search_attribute: pageOptions.searchAttribute,
              search_value: pageOptions.searchValue
            }}
          />
        </div>
      </div>

      <div className='command-filter-actions'>
        <button
          type='button'
          className='btn btn-link dosing-toggle dosing-filters-toggle'
          onClick={() => setAdvancedExpanded(expanded => !expanded)}
        >
          {t('more_filters', { scope })}
          {activeAdvancedCount > 0 && <span className='dosing-filter-count'>{activeAdvancedCount}</span>}
          <i className={`fa fa-chevron-${advancedExpanded ? 'up' : 'down'}`} />
        </button>
        {activeFilterCount > 0 && (
          <button type='button' className='btn btn-link dosing-toggle dosing-filter-clear' onClick={handleClearFilters}>
            {t('clear_filters', { scope, count: activeFilterCount })}
          </button>
        )}
      </div>

      {advancedExpanded && (
        <div className="command-filter-bar command-filter-bar-advanced">
          <FilterSelect
            label={t('dosing_type', { scope })}
            value={pageOptions.dosingType}
            options={dosingTypeOptions}
            onChange={value => updateFilter('dosingType', value)}
          />

          <FilterSelect
            label={t('product_category', { scope })}
            value={pageOptions.productCategoryId}
            emptyLabel={t('all_categories', { scope })}
            options={productCategories.map(category => ({ value: String(category.value), label: category.label }))}
            onChange={value => updateFilter('productCategoryId', value)}
          />

          <FilterSelect
            label={t('min_cycle', { scope })}
            value={pageOptions.minCycleSeconds}
            options={SLOW_CYCLE_OPTIONS.filter(option => option.value !== '')}
            emptyLabel={t('any_duration', { scope })}
            onChange={value => updateFilter('minCycleSeconds', value)}
          />

          <div className="command-filter command-filter-range">
            <span className="command-filter-label">{t('dose_size', { scope })} ({unit})</span>
            <div className="range-filter">
              <input
                type='number'
                min='0'
                className='form-control'
                placeholder={t('min', { scope })}
                value={pageOptions.minAmount}
                onChange={e => updateFilter('minAmount', e.target.value)}
              />
              <input
                type='number'
                min='0'
                className='form-control'
                placeholder={t('max', { scope })}
                value={pageOptions.maxAmount}
                onChange={e => updateFilter('maxAmount', e.target.value)}
              />
            </div>
          </div>

          <div className="command-filter command-filter-range">
            <span className="command-filter-label">{t('time_of_day', { scope })}</span>
            <div className="range-filter">
              <FilterSelectInput
                value={pageOptions.hourFrom}
                emptyLabel={t('from', { scope })}
                options={HOUR_OPTIONS}
                onChange={value => updateFilter('hourFrom', value)}
              />
              <FilterSelectInput
                value={pageOptions.hourTo}
                emptyLabel={t('to', { scope })}
                options={HOUR_OPTIONS}
                onChange={value => updateFilter('hourTo', value)}
              />
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className='alert alert-danger' role='alert'>
          {error}
        </div>
      )}

      {data.summary && <DosingSummaryTiles summary={data.summary} unit={unit} />}

      {data.feeders.length > 0 && (
        <div className='dosing-feeder-panel'>
          <button
            type='button'
            className='btn btn-link dosing-toggle dosing-feeder-toggle'
            onClick={() => setFeedersExpanded(expanded => !expanded)}
          >
            {t('by_feeder', { scope, count: data.feeders.length })}
            <i className={`fa fa-chevron-${feedersExpanded ? 'up' : 'down'}`} />
          </button>
          {feedersExpanded && <FeederBreakdown feeders={data.feeders} unit={unit} />}
        </div>
      )}

      <div className='dosing-table-scroll'>
        <table className='table table-robis dosing-history-table'>
          <thead className='thead-robis'>
            <tr>
              <th>{t('feeder', { scope })}</th>
              <th>{t('recipe_name', { scope })}</th>
              <th>{t('product_name', { scope })}</th>
              <th>{t('queued_order_id', { scope })}</th>
              <th className='dosing-num'>{t('left_before_dose', { scope })} ({unit})</th>
              <th className='dosing-num'>{t('needed_to_dose', { scope })} ({unit})</th>
              <th className='dosing-num'>{t('actual_dosed', { scope })} ({unit})</th>
              <th className='dosing-num'>{t('difference', { scope })} (%)</th>
              <th className='dosing-num'>{t('dosing_time', { scope })}</th>
              <th className='dosing-num'>{t('cycle_time', { scope })}</th>
              <th className='dosing-num'>{t('dosing_rate', { scope })} ({unit}/s)</th>
              <th>{t('result', { scope })}</th>
              <th className='dosing-when'>{t('executed_at', { scope })}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={COLUMN_COUNT} className='p-4'>
                  <div className='d-flex justify-content-center'>
                    <div className='spinner-border' role='status'>
                      <span className='visually-hidden'>Loading...</span>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              map(data.durations, (row: DosingRow, index: number) => (
                <tr key={`${row.id}-${index}`} className={row.dosing_failed ? 'dosing-row-failed' : ''}>
                  <td>{row.feeder_slot || DASH}</td>
                  <td>{row.recipe_name}</td>
                  <td>{row.product_name}</td>
                  <td>{row.queued_order_id}</td>
                  <td className='dosing-num'>{row.amount_left ?? DASH}</td>
                  <td className='dosing-num'>{row.planned_amount}</td>
                  <td className='dosing-num'>{row.total_dosed ?? row.actual_dosed}</td>
                  <td className='dosing-num'><DosingDifferencePercent percent={row.difference_percent} /></td>
                  <td className='dosing-num'>
                    {formatValue(row.duration_seconds, ' s')}
                    <span className='dosing-sample-size'> / {formatValue(row.estimated_duration, ' s')}</span>
                  </td>
                  <td className='dosing-num'>{formatValue(row.cycle_seconds, ' s')}</td>
                  <td className='dosing-num'>{formatValue(row.dosing_rate)}</td>
                  <td><DosingResult row={row} /></td>
                  <td className='dosing-when' title={timeSourceTitle(row.executed_at, TIME_SOURCE.robot)}>
                    {formatTenantDateTime(row.executed_at, localization) || DASH}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!loading && (
        <Pagination
          count={data.totalPages}
          size='large'
          page={pageOptions.currentPage}
          variant='outlined'
          shape='rounded'
          onChange={(_e, page) => handlePageChange(page)}
        />
      )}
    </div>
  )
}

export default DosingAmountHistory
